import { useState } from "react";
import { CheckCircle2, Dot, X } from "lucide-react";

import BookSummary, {
  BookMenuActions,
  ContentDetailsParameters,
} from "@components/layouts/b2c/content/BookSummary";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@ui/resizable";
import { Card } from "@ui/card";
import { getPublicMediaUrl } from "@modules/api/util/mediaHelper";
import { convertMsToHourMinSec } from "@utils/helpers/timeFormtter";
import { ShadcnAccordion } from "@components/items/display/AccordionShadcn";
import { Menu2Icon } from "@components/icons/Menu2Icon";
import { ScrollArea } from "@ui/scroll-area";
import { useAppNavigate } from "@utils/hooks/useAppNavigate";
import { Server } from "@wajeez/wajeez.microservices";

import { Reader } from "./Reader";
import TranslatedText from "@components/items/display/TranslatedText";
import { useLockedContents } from "@modules/content/hooks/useLockedContent";

export type ReaderStyle = {
  fontSize: number;
  fontType: { value: string; label: string };
  backgroundColor: string | undefined;
};

interface ContentDetailsProps {
  content: Server.ContentEntityOutput | undefined;
  params: Partial<ContentDetailsParameters>;
  progress: Server.ContentEntityProgressOutput | undefined;
  chapters: Server.ItemContentOutput[];
  playingChapterId: string | undefined;
  menu: BookMenuActions;
  onListen: () => void;
  onListenToChapter: () => void;
  onRead?: (id?: string) => void;
  readerShown?: boolean;
  onReaderClose?: () => void;
}

export const ContentDetails = ({
  content,
  params,
  progress,
  chapters,
  playingChapterId,
  menu,
  onListen,
  onListenToChapter,
  onRead,
  readerShown,
  onReaderClose,
}: ContentDetailsProps) => {
  const navigate = useAppNavigate();

  const [readerStyle, setReaderStyle] = useState<ReaderStyle>({
    fontSize: 16,
    fontType: { value: "Neo Sans Arabic", label: "Neo Sans Arabic" },
    backgroundColor: undefined,
  });

  const playingChapterIdIndex = chapters.findIndex(
    (c) => c.id === playingChapterId
  );

  const { isLockedContent } = useLockedContents(content ? [content] : []);

  const renderChapterOrSummary = () => {
    return (
      <div className="relative">
        <div
          className="absolute top-4 cursor-pointer text-black ltr:right-4 rtl:left-4 dark:text-white"
          onClick={() => {
            if (window.history.state.idx !== 0) {
              navigate(-1);
            } else {
              navigate("/");
            }
          }}
        >
          <X className="h-5 w-5" />
        </div>
        <BookSummary
          title={content?.title}
          author={content?.authors?.join(", ")}
          durationMS={content?.durationMS}
          rate={content?.rate}
          progress={Number(
            ((progress?.currentProgress || 0) * 100)?.toFixed(0)
          )}
          menu={menu}
          onListenClick={onListen}
          onReadClick={onRead}
          isLocked={isLockedContent.result?.[0]?.isLocked}
          {...params}
        />
      </div>
    );
  };

  return (
    <div className="flex h-full w-full items-center justify-start">
      <div className="h-full min-w-[300px] max-w-[300px] border-t bg-[#F9F9F9] px-3 py-8 dark:bg-[#a9a9ac]">
        <div className="cus-scrollbar-w-none h-full w-full overflow-y-auto">
          <div className="flex h-full flex-col items-center justify-start gap-y-4">
            <Card className="flex w-full items-start justify-start gap-x-3 border-none p-2 pl-4 shadow-md dark:bg-[#838488]">
              {params.thumbnail ? (
                <img
                  src={getPublicMediaUrl(params.thumbnail)}
                  alt="book"
                  className="h-[52px] w-[52px] rounded-xl"
                />
              ) : (
                <div className="h-[52px] w-[52px] rounded-xl"></div>
              )}
              <div className="gap-y flex flex-col items-start justify-center">
                <div className="text-[14px] font-medium text-black dark:text-white">
                  {content?.title}
                </div>
                <div className="gap-x flex items-center justify-center text-[12px] text-[#898A8D] dark:text-[#FFFFFFB2]">
                  <div>{content?.authors?.join(", ")}</div>
                  <Dot />
                  <div>{convertMsToHourMinSec(content?.durationMS || 0)}</div>
                </div>
              </div>
            </Card>
            {/* <Card
                className={`flex justify-start items-center px-2 dark:border-[#838488] ${
                  activeChapterIndex == -1 && "border-wajeez dark:border-wajeez"
                } w-full cursor-pointer dark:bg-[#838488] text-black dark:text-white`}
                onClick={() => setActiveChapterIndex(-1)}
              >
                <Dot size={50} />
                <div>الملخص</div>
              </Card> */}
            <ShadcnAccordion
              data={[
                {
                  id: content?.id || "",
                  trigger: (
                    <div className="flex items-center justify-center gap-x-2">
                      <Menu2Icon width={19} height={16} />
                      <TranslatedText
                        text="content-details.titles.summary content"
                        className="text-black dark:text-white"
                      />
                    </div>
                  ),
                  content: (
                    <div className="space-y-4 text-[15px]">
                      {chapters.map((chapter, index) => (
                        <Card
                          key={index}
                          className={`flex cursor-pointer items-center justify-start gap-x-4 border-none p-3 text-[#898A8D] hover:opacity-80 dark:border-[#a9a9ac] dark:bg-[#a9a9ac] dark:text-white ${
                            playingChapterIdIndex === index &&
                            "border-wajeez dark:border-wajeez"
                          } ${
                            chapter.isLocked &&
                            "pointer-events-none opacity-50 dark:opacity-50"
                          } `}
                          onClick={() => {
                            onRead && onRead(chapter.id);
                          }}
                        >
                          <div className="text-2xl">{index + 1}</div>
                          <div>{chapter.title}</div>
                          {playingChapterIdIndex === index && (
                            <CheckCircle2
                              size={28}
                              className="text-wajeez mr-auto min-w-[28px]"
                            />
                          )}
                        </Card>
                      ))}
                    </div>
                  ),
                },
              ]}
              containerClassName="w-full p-2 !rounded-xl my-5 !bg-white dark:!bg-[#838488]"
              triggerClassName="text-black dark:text-white"
            />
          </div>
        </div>
      </div>
      <div
        className={`mr-auto h-full bg-white dark:bg-black`}
        style={{
          backgroundColor: readerStyle.backgroundColor,
        }}
      >
        {!readerShown ? (
          <div className="cus-scrollbar-w-none h-full overflow-y-auto">
            {renderChapterOrSummary()}
          </div>
        ) : (
          <div
            className="h-full p-10"
            style={{
              width: "calc(100vw - 420px)",
            }}
          >
            <Reader
              title={content?.title}
              onClose={() => {
                onReaderClose && onReaderClose();
              }}
              onListenToTextClick={onListenToChapter}
              style={readerStyle}
              onStyleChange={(style) => {
                setReaderStyle(style);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
